import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/useAuth';
import { useTenantTimeZone } from '@/features/auth/useTenantTimeZone';
import { useLiveVehicles } from '@/features/live/useLiveVehicles';
import { formatDayHeading } from '@/features/trips/format';

/**
 * Canvas 1i's top-bar greeting — "Subax wanaagsan, Maxamed 👋 · <date> · fleet is
 * 74% online". Slice 15d MOVED it out of the Overview route body and into the
 * shell's single top bar, where canvas 1i draws it, beside the search and bell.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS SELF-CONTAINED, AND WHY IT IS MOUNTED ONLY ON THE OVERVIEW ROUTE.
 * ─────────────────────────────────────────────────────────────────────────────
 * The greeting's status line ("fleet is N% online") is derived from the SAME live
 * read the Overview body uses. Rather than the shell computing that (business
 * logic leaking into the chrome) or every route paying for a live query it does
 * not need, this component owns the query and is rendered by the shell ONLY when
 * the current route is the Overview. `useLiveVehicles` keys on a constant, so this
 * shares the Overview body's cache entry — one network request, not two.
 *
 * The percentage/count rule (D2) lives HERE now, once — it was moved from the
 * route, not copied. `PERCENT_MIN_FLEET` and the copy key off the SAME constant so
 * the sentence and the threshold cannot drift.
 */

// D2 — below this many vehicles the greeting shows a COUNT, not a percentage. At
// n=1 a percentage has two possible values and both read as noise ("fleet is 0%
// online" for one vehicle that has not reported yet). See the route's history.
const PERCENT_MIN_FLEET = 5;

export function OverviewGreeting() {
  const { t, i18n } = useTranslation();
  const { state } = useAuth();
  const tenantId = state.status === 'authenticated' ? state.user.tenantId : undefined;
  const query = useLiveVehicles({ tenantId });
  const { timeZone } = useTenantTimeZone();

  const firstName =
    state.status === 'authenticated' ? state.user.name.trim().split(/\s+/)[0] ?? '' : '';

  const vehicles = query.data?.vehicles ?? [];
  // `liveness?` (not `liveness.`) — the shell mounts this against whatever live
  // body is cached, and a partial/absent body must degrade to "not degraded",
  // never throw inside the top bar and blank the whole shell.
  const degraded = query.data?.liveness?.degraded ?? false;
  const total = vehicles.length;
  const reporting = vehicles.filter((v) => v.status === 'online').length;

  // The canvas's "fleet is 74% online" line — only when the live read is healthy
  // AND there is a fleet to be a percentage of. Under a degraded read every status
  // is 'unknown', so a percentage would be a fabricated 0%. Below the D2 threshold
  // the COUNT is shown instead.
  const showPercent = !degraded && total >= PERCENT_MIN_FLEET;
  const onlinePercent = showPercent ? Math.round((reporting / total) * 100) : null;
  const showCount = !degraded && total > 0 && total < PERCENT_MIN_FLEET;

  const todayLabel = formatDayHeading(i18n.language, Date.now(), timeZone);

  return (
    <div className="min-w-0">
      <h1 className="truncate font-display text-lg font-bold sm:text-xl">
        {t('shell.greeting', { name: firstName })}
      </h1>
      <p className="mt-0.5 truncate text-xs text-raad-muted sm:text-sm">
        {onlinePercent != null
          ? t('dashboard.subtitle.withOnline', { date: todayLabel, percent: onlinePercent })
          : showCount
            ? t('dashboard.subtitle.withCount', {
                date: todayLabel,
                online: reporting,
                total,
                count: total,
              })
            : todayLabel}
      </p>
    </div>
  );
}
